import { useCallback, useEffect, useRef, useState } from "react"
import { prepararTerminal, venderTef, type ResultadoTefNormalizado } from "@/lib/tef/elgin"
import { useGetLojaAtualQuery, useRegistrarPagamentoTotemMutation } from "@/api"
import { getLocalImpressaoTotem } from "@/lib/totem-config"
import { chaveDaTransacao, documentoComprovante, imprimeComprovanteTef, imprimeNoAparelho, imprimirComInsistencia } from "@/lib/impressao-totem"
import type { MetodoPagamento } from "./payment-methods"

export type FasePagamento = 'processando' | 'aprovado' | 'erro'

export interface EstadoPagamentoTef {
    metodo: MetodoPagamento | null
    cobranca: CobrancaTef | null
    fase: FasePagamento
    mensagemErro: string | null
    avisoRegistro: string | null
    /** O pagamento entrou na conta e ela não fechou: o aviso é outro, e o valor não ficou de fora. */
    contaNaoFechou: boolean
    /** O Sischef fechou a conta com este pagamento, mesmo que a cobrança fosse só de uma parte. */
    contaFechada: boolean
    registrando: boolean
    saldoRestanteCentavos: number | null
    ultimoResultado: ResultadoTefNormalizado | null
    /** `idFormaPagamento`: a forma cadastrada que o cliente tocou — o pagamento é lançado nela. */
    iniciar: (metodo: MetodoPagamento, idFormaPagamento?: number | null) => void
    resetar: () => void
}

export interface ContextoPagamento {
    valorCentavos: number
    idPedidoIntegracao: number | null
    fecharPedido: boolean
}

export type CobrancaTef = Pick<ContextoPagamento, 'valorCentavos' | 'fecharPedido'>

const AVISO_NAO_REGISTRADO = 'Pagamento aprovado, mas não foi registrado no sistema. Chame o atendente.'
const AVISO_NAO_FECHADO = 'Pagamento aprovado e registrado, mas a conta não foi fechada. Chame o atendente.'

export function usePagamentoTef({ valorCentavos, idPedidoIntegracao, fecharPedido }: ContextoPagamento): EstadoPagamentoTef {
    const [metodo, setMetodo] = useState<MetodoPagamento | null>(null)
    const [cobranca, setCobranca] = useState<CobrancaTef | null>(null)
    const [fase, setFase] = useState<FasePagamento>('processando')
    const [mensagemErro, setMensagemErro] = useState<string | null>(null)
    const [avisoRegistro, setAvisoRegistro] = useState<string | null>(null)
    const [contaNaoFechou, setContaNaoFechou] = useState(false)
    const [contaFechada, setContaFechada] = useState(false)
    const [registrando, setRegistrando] = useState(false)
    const [saldoRestanteCentavos, setSaldoRestanteCentavos] = useState<number | null>(null)
    const [ultimoResultado, setUltimoResultado] = useState<ResultadoTefNormalizado | null>(null)
    const [registrarPagamento] = useRegistrarPagamentoTotemMutation()
    const { data: loja } = useGetLojaAtualQuery()
    const servidorImpressao = loja?.impressaoLocal?.servidor ?? null
    const comprovanteLigado = imprimeComprovanteTef(loja?.configuracao)
    const cobrancaAberta = useRef(false)
    const ativo = useRef(true)

    const imprimirComprovante = useCallback((resultado: ResultadoTefNormalizado): void => {
        const comprovante = comprovanteLigado && imprimeNoAparelho() ? documentoComprovante(resultado.comprovante) : null
        if (!comprovante) return

        const chave = `checkout:TEF:${chaveDaTransacao(resultado.uniqueId, resultado.nsu, resultado.dataTransacao)}`
        void imprimirComInsistencia(chave, async () => [comprovante], servidorImpressao)
    }, [servidorImpressao, comprovanteLigado])

    const registrarNaComanda = useCallback(async (metodoTef: MetodoPagamento['metodoTef'], resultado: ResultadoTefNormalizado, cobrado: CobrancaTef, idFormaPagamento: number | null): Promise<void> => {
        if (ativo.current) setRegistrando(true)

        try {
            if (idPedidoIntegracao == null) {
                console.error('[pagamento-totem] pedido sem idPedidoIntegracao; pagamento aprovado NÃO registrado', resultado)
                if (ativo.current) setAvisoRegistro(AVISO_NAO_REGISTRADO)
                return
            }

            const noAparelho = imprimeNoAparelho()
            const resposta = await registrarPagamento({
                idPedidoIntegracao,
                metodo: metodoTef,
                idFormaPagamento,
                valorCentavos: cobrado.valorCentavos,
                fecharPedido: cobrado.fecharPedido,
                uniqueId: resultado.uniqueId ?? null,
                nsu: resultado.nsu ?? null,
                bandeira: resultado.bandeira ?? null,
                authorizationCode: resultado.authorizationCode ?? null,
                adquirente: resultado.adquirente ?? null,
                tipoCartao: resultado.tipoCartao ?? null,
                dataTransacao: resultado.dataTransacao ?? null,
                comprovante: resultado.comprovante ?? null,
                localImpressao: noAparelho ? null : getLocalImpressaoTotem() || null,
                ...(noAparelho ? { imprimeNoAparelho: true } : {}),
                raw: resultado.raw,
            }).unwrap()

            if (ativo.current && resposta?.saldoRestanteCentavos != null) {
                setSaldoRestanteCentavos(resposta.saldoRestanteCentavos)
            }

            if (ativo.current && resposta?.pago) setContaFechada(true)

            if (resposta?.fechamentoRecusado) {
                console.warn('[pagamento-totem] pagamento registrado, fechamento da conta recusado', resposta)
                if (ativo.current) {
                    setContaNaoFechou(true)
                    setAvisoRegistro(resposta.mensagem ?? AVISO_NAO_FECHADO)
                }
            }
        } catch (err) {
            console.error('[pagamento-totem] falha ao registrar pagamento aprovado na comanda', err)
            const mensagem = (err as { data?: { mensagem?: string } })?.data?.mensagem
            if (ativo.current) setAvisoRegistro(mensagem ? `Pagamento aprovado, mas não foi registrado: ${mensagem}` : AVISO_NAO_REGISTRADO)
        } finally {
            if (ativo.current) setRegistrando(false)
        }
    }, [idPedidoIntegracao, registrarPagamento])

    const iniciar = useCallback((metodoEscolhido: MetodoPagamento, idFormaPagamento: number | null = null): void => {
        // Uma cobrança por vez, até recomeçar: com o botão da forma ainda focado, um Enter de leitor abriria outra venda.
        if (cobrancaAberta.current) return
        cobrancaAberta.current = true

        // A tela recalcula valor e fechamento a cada render; a cobrança vale o que era no toque.
        const cobrado: CobrancaTef = { valorCentavos, fecharPedido }

        setMetodo(metodoEscolhido)
        setCobranca(cobrado)
        setMensagemErro(null)
        setAvisoRegistro(null)
        setContaNaoFechou(false)
        setContaFechada(false)
        setFase('processando')

        venderTef(metodoEscolhido.metodoTef, cobrado.valorCentavos)
            .then(async (resultado) => {
                if (!ativo.current) return

                if (!resultado.aprovado) {
                    setMensagemErro(resultado.mensagem ?? 'Pagamento não aprovado. Tente novamente ou escolha outra forma.')
                    setFase('erro')
                    return
                }

                setUltimoResultado(resultado)
                setFase('aprovado')
                imprimirComprovante(resultado)
                await registrarNaComanda(metodoEscolhido.metodoTef, resultado, cobrado, idFormaPagamento)
            })
            .catch((err: unknown) => {
                if (!ativo.current) return
                setMensagemErro(mensagemDeErro(err))
                setFase('erro')
            })
    }, [valorCentavos, fecharPedido, registrarNaComanda, imprimirComprovante])

    const resetar = useCallback((): void => {
        cobrancaAberta.current = false
        setMetodo(null)
        setCobranca(null)
        setMensagemErro(null)
        setAvisoRegistro(null)
        setContaNaoFechou(false)
        setContaFechada(false)
        setRegistrando(false)
        setSaldoRestanteCentavos(null)
        setUltimoResultado(null)
        setFase('processando')
    }, [])

    useEffect(() => {
        ativo.current = true
        void prepararTerminal()
        return () => { ativo.current = false }
    }, [])

    useEffect(() => {
        if (fase !== 'processando' || !metodo) return

        const bloquear = (event: BeforeUnloadEvent): void => {
            event.preventDefault()
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', bloquear)
        return () => window.removeEventListener('beforeunload', bloquear)
    }, [fase, metodo])

    return { metodo, cobranca, fase, mensagemErro, avisoRegistro, contaNaoFechou, contaFechada, registrando, saldoRestanteCentavos, ultimoResultado, iniciar, resetar }
}

function mensagemDeErro(err: unknown): string {
    if (err instanceof Error && err.message) return err.message
    return 'Não foi possível se comunicar com o terminal de pagamento. Verifique a maquininha e tente novamente.'
}
